import { useContext, useRef } from 'react';
import type { FunctionComponent, Ref, MouseEvent, ReactNode, KeyboardEvent } from 'react';

import type { BaseProps, ForwardProps } from '../../types';
import Alert from '../Badges/Alert';
import Button from '../Button';
import Flex from '../Flex';
import Icon, { registerIcon } from '../Icon';
import * as pinIcon from '../Icon/icons/pin.icon';
import * as pinSolidIcon from '../Icon/icons/pin-solid.icon';
import MetaList from '../MetaList';
import EmptyState from '../EmptyState';
import Progress from '../Progress';
import SummaryItem from '../SummaryItem';
import Text from '../Text';
import { useI18n } from '../../hooks';

import { StyledLoading } from './AppShell.styles';
import {
  StyledAppShellListWrapper,
  StyledAppShellList,
  StyledAppShellSummaryList,
  StyledAppShellMetaList,
  StyledNotificationPrimary,
  StyledNotificationSecondary,
  StyledAppShellPinButton,
  EmptyStateColorContrast,
  StyledAppShellListSection,
  StyledAppShellListHeading,
  StyledNotificationVisual
} from './AppShellList.styles';
import { StyledDrawerViewAll } from './Drawer.styles';
import { AppShellDrawerContext } from './AppShellContext';

registerIcon(pinIcon, pinSolidIcon);

export type ListInteractionEvent = MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>;
export interface ListProps {
  /** An id representing the item. */
  id: string;
  ref?: Ref<HTMLDivElement>;
  items: (AppShellListItemProps & ForwardProps)[];
  headingText?: string;
  emptyText: string;
  loading?: boolean;
  viewAll?: {
    onClick?: (e: MouseEvent) => void;
    href?: string;
  } & ForwardProps;
  newNotifications?: number;
  displayPins?: boolean;
  onItemClick?: (id: string, event: ListInteractionEvent) => void;
  onItemPinToggle?: (id: string) => void;
}
export interface AppShellListProps {
  listView: ListProps[];
}
export interface AppShellListItemProps extends BaseProps {
  /** An id representing the item. */
  id: string;
  /** The primary text for the item. */
  primary: string;
  /** Secondary text that will be rendered as a Meta List. */
  secondary?: string[];
  /** A visual associated with the item. */
  visual?: ReactNode;
  /** A flag indicating if the item has been read. */
  unread?: boolean;
  /** Icon for displaying at the end of items */
  pinned?: boolean;
  /** URL or DOM id to navigate to. */
  href?: string;
  /** The onClick handler for the item. */
  onClick?: (id: string, event: ListInteractionEvent) => void;
  /** The handler hat is called when a pin icon is toggled on an item. */
  onPinToggle?: (id: string) => void;
}

const List = ({
  headingText,
  emptyText,
  items,
  loading = false,
  displayPins = false,
  onItemClick,
  onItemPinToggle,
  viewAll: viewAllProps
}: ListProps) => {
  const t = useI18n();
  const { closeDrawer } = useContext(AppShellDrawerContext);
  const pinButtons = useRef<Record<string, HTMLElement>>({});

  const renderContent = items.length ? (
    <Flex as={StyledAppShellList} item={{ grow: 1 }}>
      {items.map(
        ({
          id,
          primary,
          secondary,
          visual,
          unread,
          href,
          onClick,
          pinned,
          onPinToggle,
          ...restProps
        }: AppShellListItemProps) => {
          const onSummaryItemClick = (e: ListInteractionEvent) => {
            onClick?.(id, e);
            onItemClick?.(id, e);

            if (!pinButtons.current[id]?.contains(e.target as Node)) {
              closeDrawer();
            }
          };

          return (
            <StyledAppShellSummaryList key={id}>
              <SummaryItem
                {...restProps}
                as={href ? 'a' : 'button'}
                primary={
                  <StyledNotificationPrimary unread={unread}>{primary}</StyledNotificationPrimary>
                }
                secondary={
                  secondary?.length ? (
                    <StyledNotificationSecondary unread={unread}>
                      <MetaList as={StyledAppShellMetaList} items={secondary} />
                    </StyledNotificationSecondary>
                  ) : undefined
                }
                visual={
                  visual || unread ? (
                    <StyledNotificationVisual unread={unread} visualIncluded={!!visual}>
                      {visual} <Alert variant='urgent' />
                    </StyledNotificationVisual>
                  ) : undefined
                }
                href={href}
                onClick={onSummaryItemClick}
                onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onSummaryItemClick(e);
                  }
                }}
                actions={
                  displayPins ? (
                    <Button
                      ref={el => {
                        if (!el) delete pinButtons.current[id];
                        else pinButtons.current[id] = el;
                      }}
                      icon
                      as={StyledAppShellPinButton}
                      forwardedAs='div'
                      onClick={() => {
                        onPinToggle?.(id);
                        onItemPinToggle?.(id);
                      }}
                      onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                        if (e.key === 'Enter') {
                          onPinToggle?.(id);
                          onItemPinToggle?.(id);
                        }
                      }}
                      role='button'
                      tabIndex={0}
                      label={t(pinned ? 'app_shell_unpin' : 'app_shell_pin')}
                      aria-label={`${t(pinned ? 'app_shell_unpin' : 'app_shell_pin')} - ${primary}`}
                    >
                      <Icon name={pinned ? 'pin-solid' : 'pin'} />
                    </Button>
                  ) : undefined
                }
                tabIndex={0}
              />
            </StyledAppShellSummaryList>
          );
        }
      )}
    </Flex>
  ) : (
    <Flex
      as={EmptyStateColorContrast}
      container={{ justify: 'center', alignItems: 'center' }}
      item={{ grow: 1 }}
    >
      <EmptyState message={emptyText} />
    </Flex>
  );

  return (
    <Flex as={StyledAppShellListSection} container={{ direction: 'column' }}>
      {headingText && (
        <Text variant='h3' as={StyledAppShellListHeading}>
          {headingText}
        </Text>
      )}
      {loading ? (
        <Flex container={{ justify: 'center', alignItems: 'center' }} item={{ grow: 1 }}>
          <Progress as={StyledLoading} placement='inline' />
        </Flex>
      ) : (
        renderContent
      )}
      {(viewAllProps?.href || viewAllProps?.onClick) && !!items.length && !loading && (
        <StyledDrawerViewAll {...viewAllProps} as={viewAllProps.href ? 'a' : 'button'}>
          {t('view_all')}
        </StyledDrawerViewAll>
      )}
    </Flex>
  );
};
const AppShellList: FunctionComponent<AppShellListProps> = ({ listView }) => {
  return (
    <Flex as={StyledAppShellListWrapper} container={{ direction: 'column' }}>
      {listView.map(listItem => (
        <List {...listItem} key={listItem.id} />
      ))}
    </Flex>
  );
};

export default AppShellList;
